import type {
  CharacterIndexAttestation,
  CharacterIndexDraftContribution,
  CharacterIndexEntry,
  CharacterIndexEntryStatusPhase,
  CharacterIndexInput,
  CharacterIndexJsonValue,
  CharacterIndexNameNote,
  CharacterIndexOptions,
  CharacterIndexOutput,
  CharacterIndexRowCellInput,
  CharacterIndexSourceAnchor,
  CharacterIndexWitnessInput,
  RunningIndex,
} from '@variorum/character-index'
import type { Witness } from '../store/booksStore'
import { preprocessLedgerText, type WitnessUnitTextRecord } from './ledgerTextPreprocessing'
import { getRowLabel, type AnchoredRow, type AnchoredRowSceneMetadata, type UnitMapping } from '../types/anchoredRowTypes'
import type { ChapterCharacterLayer, CharacterEntity, CharacterOccurrence } from '../types/characterLayerTypes'
import {
  characterOccurrenceRefersTo,
  getCharacterFigureKind,
  getCharacterOccurrenceReferentIds,
} from './characterLayerHelpers'

export const CHARACTER_INDEX_INPUT_SCHEMA_VERSION = 'variorum.character-index.input.v1'
export const CHARACTER_INDEX_RUNNING_INDEX_SCHEMA_VERSION = 'variorum.character-index.output.from-light-layer.v2'

type CharacterIndexMentionType = 'name' | 'coref'
type CharacterIndexMentionSourceAnchor = CharacterIndexSourceAnchor & {
  mentionType?: CharacterIndexMentionType | null
}

export interface BuildCharacterIndexInputArgs {
  projectId: string
  workId: string
  workTitle: string
  bookId: string
  chapterId: string
  chapterLabel: string
  sourceStateId?: string
  exportedAt?: string
  witnesses: Witness[]
  rows: AnchoredRow[]
  textLookupByWitnessId?: Map<string, Map<string, WitnessUnitTextRecord>>
  chapterLayer?: ChapterCharacterLayer | null
  runningIndex?: RunningIndex | null
  options?: CharacterIndexOptions
}

const trimToNull = (value: string | undefined | null): string | null => {
  const trimmed = typeof value === 'string' ? value.trim() : ''
  return trimmed || null
}

const trimToString = (value: string | undefined | null): string => (
  typeof value === 'string' ? value.trim() : ''
)

const normalizeText = (value: string): string => value.replace(/\s+/g, ' ').trim()

const isFiniteNumber = (value: unknown): value is number => (
  typeof value === 'number' && Number.isFinite(value)
)

const isActiveStatus = (status: string): boolean => status !== 'rejected'

const normalizeMentionType = (value: unknown): CharacterIndexMentionType | undefined => (
  value === 'name' || value === 'coref' ? value : undefined
)

const buildOccurrenceSourceAnchor = (occurrence: CharacterOccurrence): CharacterIndexMentionSourceAnchor => {
  const mentionType = normalizeMentionType(occurrence.metadata?.mention_type || occurrence.metadata?.mentionType)
  return {
    witnessId: occurrence.locator.witnessId,
    rowId: occurrence.locator.rowId,
    ...(mentionType ? { mentionType } : {}),
    ...(isFiniteNumber(occurrence.locator.localStart) ? { localStart: occurrence.locator.localStart } : {}),
    ...(isFiniteNumber(occurrence.locator.localEnd) ? { localEnd: occurrence.locator.localEnd } : {}),
    ...(isFiniteNumber(occurrence.confidence) ? { confidence: occurrence.confidence } : {}),
  }
}

const sortRows = (rows: AnchoredRow[]): AnchoredRow[] => (
  [...rows].sort((left, right) => (
    left.position - right.position ||
    left.labelNumber - right.labelNumber ||
    left.id.localeCompare(right.id)
  ))
)

const sanitizeJsonValue = (value: unknown): CharacterIndexJsonValue | undefined => {
  if (value === null || typeof value === 'boolean' || typeof value === 'number' || typeof value === 'string') {
    return value
  }
  if (Array.isArray(value)) {
    return value
      .map(item => sanitizeJsonValue(item))
      .filter((item): item is CharacterIndexJsonValue => item !== undefined)
  }
  if (typeof value === 'object' && value) {
    const output: Record<string, CharacterIndexJsonValue> = {}
    Object.entries(value as Record<string, unknown>).forEach(([key, item]) => {
      const sanitized = sanitizeJsonValue(item)
      if (sanitized !== undefined) {
        output[key] = sanitized
      }
    })
    return output
  }
  return undefined
}

const getSceneMetadata = (row: AnchoredRow): AnchoredRowSceneMetadata | null => {
  const scene = row.metadata?.scene
  if (!scene || typeof scene !== 'object' || Array.isArray(scene)) {
    return null
  }
  const title = typeof scene.title === 'string' ? scene.title.trim() : ''
  const sceneId = typeof scene.sceneId === 'string' ? scene.sceneId.trim() : ''
  if (!title && !sceneId && typeof scene.sceneNumber !== 'number' && typeof scene.sceneOrdinal !== 'number') {
    return null
  }
  return {
    ...scene,
    sceneId: sceneId || undefined,
    title: title || (sceneId || 'Scene'),
  }
}

const getSceneId = (scene: AnchoredRowSceneMetadata | null): string | null => {
  if (!scene) {
    return null
  }
  if (typeof scene.sceneId === 'string' && scene.sceneId.trim()) {
    return scene.sceneId.trim()
  }
  if (typeof scene.sceneOrdinal === 'number') {
    return `scene-ordinal-${scene.sceneOrdinal}`
  }
  if (typeof scene.sceneNumber === 'number') {
    return `scene-number-${scene.sceneNumber}`
  }
  const title = typeof scene.title === 'string' ? scene.title.trim().toLowerCase() : ''
  return title ? `scene-title-${title}` : null
}

const getSceneLabel = (scene: AnchoredRowSceneMetadata | null, sceneId: string | null): string | null => {
  if (!scene) {
    return null
  }
  const title = typeof scene.title === 'string' ? scene.title.trim() : ''
  if (title && title !== 'Scene') {
    return title
  }
  const ordinal = typeof scene.sceneOrdinal === 'number'
    ? scene.sceneOrdinal
    : typeof scene.sceneNumber === 'number'
      ? scene.sceneNumber
      : null
  if (ordinal !== null) {
    return `Scene ${ordinal}`
  }
  return sceneId || null
}

const hashString = (value: string): string => {
  let hash = 2166136261
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index)
    hash = Math.imul(hash, 16777619)
  }
  return (hash >>> 0).toString(36)
}

export const buildCharacterIndexSourceStateId = ({
  chapterId,
  witnesses,
  rows,
}: {
  chapterId: string
  witnesses: Witness[]
  rows: AnchoredRow[]
}): string => {
  const payload = {
    chapterId,
    witnesses: witnesses.map(witness => ({
      id: witness.id,
      sigla: witness.sigla,
      updatedAt: witness.last_modified,
    })),
    rows: sortRows(rows).map(row => ({
      id: row.id,
      position: row.position,
      status: row.status,
      updatedAt: row.updatedAt,
      sceneId: getSceneId(getSceneMetadata(row)),
      unitMappings: row.unitMappings.map(mapping => ({
        witnessId: mapping.witnessId,
        unitUuid: mapping.unitUuid,
        unitLabel: mapping.unitLabel,
        textSnapshot: mapping.textSnapshot || '',
        translationStatus: mapping.translationStatus,
        excluded: Boolean(mapping.excluded),
      })),
    })),
  }
  return `ledger-safe:${chapterId}:${hashString(JSON.stringify(payload))}`
}

export const mapWitnessToCharacterIndexInput = (witness: Witness): CharacterIndexWitnessInput => ({
  witnessId: witness.id,
  sigla: witness.sigla,
  label: trimToNull(witness.description) || witness.sigla,
  description: trimToNull(witness.description),
  language: trimToNull(witness.language),
  manuscriptDate: trimToNull(witness.manuscript_date),
  library: trimToNull(witness.library),
  shelfmark: trimToString(witness.shelfmark) || witness.sigla,
  witnessType: trimToNull(witness.witness_type),
  transcriptionType: trimToNull(witness.transcription_type),
})

const getMappingForCell = (row: AnchoredRow, witnessId: string): UnitMapping | null => (
  row.unitMappings.find(mapping => mapping.witnessId === witnessId && !mapping.excluded) || null
)

const hasExcludedMappingForCell = (row: AnchoredRow, witnessId: string): boolean => (
  row.unitMappings.some(mapping => mapping.witnessId === witnessId && Boolean(mapping.excluded))
)

const resolveCharacterIndexMappingText = (
  mapping: UnitMapping | null,
  textLookupByWitnessId?: Map<string, Map<string, WitnessUnitTextRecord>>
): string => {
  if (!mapping) {
    return ''
  }
  const canonical = mapping.unitUuid
    ? textLookupByWitnessId?.get(mapping.witnessId)?.get(mapping.unitUuid)
    : null
  if (canonical?.workspaceDisplayText?.trim()) {
    return normalizeText(canonical.workspaceDisplayText)
  }
  const snapshot = mapping.textSnapshot || ''
  return snapshot.trim()
    ? normalizeText(preprocessLedgerText(snapshot).workspaceDisplayText)
    : ''
}

export const mapRowCellToCharacterIndexInput = ({
  row,
  textLookupByWitnessId,
  witness,
}: {
  row: AnchoredRow
  textLookupByWitnessId?: Map<string, Map<string, WitnessUnitTextRecord>>
  witness: Witness
}): CharacterIndexRowCellInput => {
  const mapping = getMappingForCell(row, witness.id)
  const text = resolveCharacterIndexMappingText(mapping, textLookupByWitnessId)
  const confidence = mapping?.confidence
  const isGap = !mapping || hasExcludedMappingForCell(row, witness.id) || mapping.translationStatus === 'gap'
  return {
    witnessId: witness.id,
    text,
    ...(text ? { normalizedText: text } : {}),
    ...(mapping?.unitUuid ? { unitUuid: mapping.unitUuid, unitId: mapping.unitUuid } : {}),
    ...(mapping?.unitLabel ? { unitLabel: mapping.unitLabel } : {}),
    ...(isGap ? { gap: true } : {}),
    ...(mapping?.translationStatus === 'transposed' ? { transposed: true } : {}),
    ...(isFiniteNumber(confidence) ? { confidence } : {}),
  }
}

const mapOriginToContributionSource = (
  origin: CharacterEntity['origin'] | CharacterOccurrence['origin'],
): CharacterIndexDraftContribution['source'] => {
  if (origin === 'manual') {
    return 'editorial'
  }
  if (origin === 'derived') {
    return 'inferred'
  }
  if (origin === 'imported') {
    return 'imported'
  }
  return 'llm'
}

const mapCharacterStatusToEntryPhase = (character: CharacterEntity): CharacterIndexEntryStatusPhase => {
  if (character.status === 'confirmed' || character.status === 'edited') {
    return 'accepted'
  }
  return 'candidate_draft'
}

const mapCharacterStatusToDraftDecision = (
  character: CharacterEntity,
): CharacterIndexDraftContribution['decision'] => (
  character.status === 'confirmed' || character.status === 'edited' ? 'accepted' : 'pending'
)

const inferNameNote = (character: CharacterEntity): CharacterIndexNameNote => {
  const metadataNote = character.metadata?.name_note
  if (typeof metadataNote === 'string' && metadataNote.trim()) {
    return metadataNote.trim() as CharacterIndexNameNote
  }
  return character.display_name.trim().startsWith('[') ? 'descriptor' : 'personal'
}

const mapOccurrenceToAttestation = ({
  occurrence,
  workId,
  chapterId,
}: {
  occurrence: CharacterOccurrence
  workId: string
  chapterId: string
}): CharacterIndexAttestation => ({
  work: workId,
  location: {
    witness: occurrence.locator.witnessId,
    chapter: chapterId,
  },
  sourceAnchors: [buildOccurrenceSourceAnchor(occurrence)],
  role: occurrence.role_labels.length > 0 ? occurrence.role_labels.join(', ') : null,
  note: occurrence.notes || occurrence.summary || occurrence.label || null,
})

export const projectChapterCharacterLayerToRunningIndex = ({
  layer,
  workId,
  chapterId,
}: {
  layer: ChapterCharacterLayer
  workId: string
  chapterId: string
}): CharacterIndexOutput => {
  const activeCharacters = layer.characters.filter(character => isActiveStatus(character.status))
  const activeCharacterIds = new Set(activeCharacters.map(character => character.character_id))
  const activeOccurrences = layer.occurrences.filter(occurrence => (
    isActiveStatus(occurrence.status) &&
    getCharacterOccurrenceReferentIds(occurrence).some(characterId => activeCharacterIds.has(characterId))
  ))

  const entries: CharacterIndexEntry[] = activeCharacters.map(character => {
    const metadata = character.metadata || {}
    const transliterationVariants = Array.isArray(metadata.transliteration_variants)
      ? metadata.transliteration_variants.filter((value): value is string => typeof value === 'string')
      : []
    const attestations = activeOccurrences
      .filter(occurrence => characterOccurrenceRefersTo(occurrence, character.character_id))
      .map(occurrence => mapOccurrenceToAttestation({ occurrence, workId, chapterId }))
    const decision = mapCharacterStatusToDraftDecision(character)
    const fields = [
      'lemma',
      'names',
      ...(attestations.length > 0 ? ['attestations'] : []),
    ]

    return {
      id: character.character_id,
      kind: getCharacterFigureKind(character) || 'person',
      lemma: character.display_name,
      names: {
        primary: character.display_name,
        variants: character.aliases || [],
        note: inferNameNote(character),
      },
      transliteration: {
        primary: typeof metadata.transliteration === 'string' ? metadata.transliteration : '',
        variants: transliterationVariants,
      },
      attestations,
      variant_candidates: [],
      variant: [],
      adapted_in: [],
      adapted_from: [],
      status: {
        phase: mapCharacterStatusToEntryPhase(character),
        activeDraftId: `${character.character_id}:current`,
        drafts: [{
          draftId: `${character.character_id}:current`,
          completeness: attestations.length > 0 ? 'complete' : 'partial',
          decision,
          contributions: [{
            contributionId: `${character.character_id}:current:${character.origin}`,
            source: mapOriginToContributionSource(character.origin),
            fields,
            decision,
            ...(character.notes ? { note: character.notes } : {}),
          }],
          ...(character.notes ? { note: character.notes } : {}),
        }],
      },
      relations: [],
    }
  })

  return {
    schemaVersion: CHARACTER_INDEX_RUNNING_INDEX_SCHEMA_VERSION,
    entries,
  }
}

export const buildCharacterIndexInput = ({
  projectId,
  workId,
  workTitle,
  bookId,
  chapterId,
  chapterLabel,
  sourceStateId,
  exportedAt,
  witnesses,
  rows,
  textLookupByWitnessId,
  chapterLayer,
  runningIndex,
  options,
}: BuildCharacterIndexInputArgs): CharacterIndexInput => {
  const orderedRows = sortRows(rows)
  const document = {
    projectId,
    workId,
    workTitle,
    bookId,
    chapterId,
    chapterLabel,
    sourceStateId: sourceStateId || buildCharacterIndexSourceStateId({ chapterId, witnesses, rows: orderedRows }),
    exportedAt: exportedAt || new Date().toISOString(),
  }

  return {
    schemaVersion: CHARACTER_INDEX_INPUT_SCHEMA_VERSION,
    document,
    witnesses: witnesses.map(mapWitnessToCharacterIndexInput),
    alignedRows: orderedRows.map(row => {
      const metadata = row.metadata as Record<string, unknown> | undefined
      const sceneMetadata = getSceneMetadata(row)
      const sceneId = getSceneId(sceneMetadata)
      const sceneLabel = getSceneLabel(sceneMetadata, sceneId)
      const scene = sanitizeJsonValue(metadata?.scene)
      const subtale = sanitizeJsonValue(metadata?.subtale)
      return {
        rowId: row.id,
        rowLabel: getRowLabel(row),
        rowPosition: row.position,
        rowStatus: row.status,
        ...(sceneId ? { sceneId } : {}),
        ...(sceneLabel ? { sceneLabel } : {}),
        ...(scene !== undefined ? { scene } : {}),
        ...(subtale !== undefined ? { subtale } : {}),
        cells: witnesses.map(witness => mapRowCellToCharacterIndexInput({ row, textLookupByWitnessId, witness })),
      }
    }),
    ...(runningIndex ? { runningIndex } : {}),
    ...(!runningIndex && chapterLayer ? {
      runningIndex: projectChapterCharacterLayerToRunningIndex({
        layer: chapterLayer,
        workId,
        chapterId,
      }),
    } : {}),
    ...(options ? { options } : {}),
  }
}
